import { v4 } from 'uuid';

import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';
import { AiContentVoiceoverType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentVoiceoverTypeEnum';
import { Origin } from '@videoblocks/events-ts/lib/storyblocks/content/ai/OriginEnum';

import { CommonRecordFactory } from '../shared/CommonRecordFactory';
import parseDevice from '../shared/parseDevice';

/**
 * Common options shared by both Generate and Download AI Content events
 */
export interface AiContentEventCommonOptions {
    // these are the identifiers used to tie events together and are stored as browser cookies
    aiContentId: string;
    lastAiContentId?: string | null;

    // voiceover-specific details
    voiceId?: string | null;
    aiContentVoiceoverType?: keyof typeof AiContentVoiceoverType | null;

    // User and session info
    visitorCookieId: string | null;
    sessionToken?: string | undefined;

    // URL and device context
    currentUrl: URL;
    referrer: URL | undefined;
    headers: Headers;

    // Optional geo/IP data
    geo?: { countryCode: string; country: string; continent: string } | null;
    ipAddress?: string | null;
}

/**
 * properties shared by all AiContentEvents
 */
export interface AiContentEventCommonProperties {
    meta: {
        uuid: string;
        emitted: number;
        origin: Origin;
        trace: null;
    };
    user: Awaited<ReturnType<typeof CommonRecordFactory.user>>;
    device: ReturnType<typeof parseDevice>;
    urls: {
        currentUrl: string;
        referrerUrl?: string | null;
    };
    geo: { countryCode: string; country: string; continent: string } | null;
    ipAddress: string | null;
    aiContentIdentifiers: {
        aiContentId: string;
        aiContentType: AiContentType;
        lastAiContentId: string | null;
    };
    aiContentVoiceoverDetails: {
        voiceId: string | null;
        aiContentVoiceoverType: AiContentVoiceoverType | null;
    } | null;
}

/**
 * Builds the common properties shared by both Generate and Download events.
 * This reduces code duplication between the two factory functions.
 */
export async function buildCommonAiContentEventProperties(
    options: AiContentEventCommonOptions,
): Promise<AiContentEventCommonProperties> {
    const {
        aiContentId,
        lastAiContentId = null,
        voiceId = null,
        aiContentVoiceoverType = null,
        visitorCookieId,
        sessionToken,
        currentUrl,
        referrer,
        headers,
        geo = null,
        ipAddress = null,
    } = options;

    const referrerUrl = referrer?.href;

    const meta = {
        uuid: v4(),
        emitted: Date.now(),
        origin: Origin.STORYBLOCKS_NEXTJS,
        trace: null,
    };

    const user = await CommonRecordFactory.user(sessionToken, visitorCookieId);

    const device = parseDevice(headers, referrerUrl);

    const urls = { currentUrl: currentUrl.href, referrerUrl };

    const aiContentIdentifiers = {
        aiContentId,
        aiContentType: AiContentType.AI_VOICEOVER,
        lastAiContentId,
    };

    const aiContentVoiceoverDetails =
        voiceId !== null || aiContentVoiceoverType !== null
            ? {
                  voiceId,
                  aiContentVoiceoverType: aiContentVoiceoverType
                      ? AiContentVoiceoverType[aiContentVoiceoverType]
                      : null,
              }
            : null;

    return {
        meta,
        user,
        device,
        urls,
        geo,
        ipAddress,
        aiContentIdentifiers,
        aiContentVoiceoverDetails,
    };
}
